import { Reveal, RevealStagger, StaggerItem } from "../components/Reveal";
import { FinalCTA } from "../components/FinalCTA";
import { MagneticButton } from "../components/MagneticButton";
import { CaseStudyVisual } from "../components/CaseStudyVisual";
import { FloatingMotif } from "../components/FloatingMotif";
import { CASE_STUDIES, TESTIMONIALS } from "../data/site";

export default function Work() {
    return (
        <div data-testid="page-work">
            {/* Header */}
            <section className="relative pt-12 md:pt-20 pb-20 md:pb-28 border-b border-[#dddddd] overflow-hidden">
                <FloatingMotif />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-8">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="h-px w-10 bg-[#111111]" />
                                    <span className="text-[10px] tracking-[0.28em] uppercase font-medium">
                                        Work · Selected Studies
                                    </span>
                                </div>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tight">
                                    Quiet work,
                                    <br />
                                    <em>measurable outcomes.</em>
                                </h1>
                            </Reveal>
                        </div>
                        <div className="md:col-span-4">
                            <Reveal delay={0.15}>
                                <p className="text-lg text-[#444444] leading-relaxed">
                                    A small selection of business outcomes
                                    we’ve supported. Names and identifying
                                    details are kept private where partners
                                    prefer it.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Case Studies — wireframe before/after with metrics */}
            <section className="py-20 md:py-28">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 space-y-24 md:space-y-36">
                    {CASE_STUDIES.map((c, i) => (
                        <article
                            key={c.title}
                            data-testid={`case-study-${i}`}
                            className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start"
                        >
                            <div className="md:col-span-5 md:sticky md:top-28">
                                <Reveal>
                                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                        Study № {String(i + 1).padStart(2, "0")} · {c.sector}
                                    </p>
                                    <h2 className="mt-5 font-serif text-3xl md:text-5xl text-[#111111] leading-[1.05] tracking-tight">
                                        {c.title}
                                    </h2>
                                    <p className="mt-6 font-serif italic text-2xl text-[#444444]">
                                        {c.outcome}
                                    </p>
                                    <p className="mt-6 text-[#444444] text-lg leading-relaxed">
                                        {c.body}
                                    </p>
                                    <div className="mt-8 flex flex-wrap gap-2">
                                        {c.tags.map((t) => (
                                            <span
                                                key={t}
                                                className="text-[10px] tracking-[0.28em] uppercase border border-[#111111] px-3 py-1.5"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </Reveal>
                            </div>
                            <div className="md:col-span-7">
                                <CaseStudyVisual metrics={c.metrics} />
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* Voices */}
            <section className="bg-[#111111] text-white py-24 md:py-32 relative overflow-hidden">
                <FloatingMotif variant="dark" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="flex items-center gap-6 mb-12">
                        <span className="text-[10px] tracking-[0.28em] uppercase text-white/60">§ Voices</span>
                        <span className="h-px w-12 bg-white inline-block" />
                        <span className="text-[10px] tracking-[0.28em] uppercase">Partner testimony</span>
                    </div>
                    <RevealStagger className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <StaggerItem key={t.name}>
                                <figure
                                    data-testid={`work-testimonial-${i}`}
                                    className="border border-white/15 p-8 md:p-10 h-full hover:border-white hover:-translate-y-1 transition-all duration-700"
                                >
                                    <span aria-hidden className="font-serif text-6xl leading-none text-white/20">“</span>
                                    <blockquote className="mt-2 font-serif text-xl md:text-2xl leading-snug">
                                        {t.quote}
                                    </blockquote>
                                    <figcaption className="mt-8 pt-5 border-t border-white/15">
                                        <p className="font-medium text-sm">{t.name}</p>
                                        <p className="text-[10px] tracking-[0.28em] uppercase text-white/60 mt-1">
                                            {t.role} · {t.company}
                                        </p>
                                    </figcaption>
                                </figure>
                            </StaggerItem>
                        ))}
                    </RevealStagger>
                </div>
            </section>

            {/* CTA strip */}
            <section className="py-20 md:py-28">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-8">
                            <Reveal>
                                <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">
                                    Want to be the next quiet
                                    <br />
                                    <em>case study?</em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-4 flex flex-wrap gap-4 md:justify-end">
                            <MagneticButton to="/contact" testid="work-cta-primary">
                                Book a Strategy Call
                            </MagneticButton>
                            <MagneticButton to="/services" testid="work-cta-secondary" variant="outline">
                                View Services
                            </MagneticButton>
                        </div>
                    </div>
                </div>
            </section>

            <FinalCTA />
        </div>
    );
}
